export type StepType =
  // Text
  | 'headline' | 'text'
  // Input
  | 'multiple_choice' | 'picture_choice' | 'input_field' | 'phone_number'
  | 'date_picker' | 'textarea' | 'simple_checkbox' | 'select' | 'scale'
  | 'icon_rating' | 'range_slider' | 'upload' | 'form' | 'address_map' | 'email'
  // Navigation
  | 'links' | 'loader' | 'list_loader'
  // Buttons
  | 'continue_button' | 'submit_button' | 'back_button' | 'link_button'
  | 'universal_button' | 'navigation'
  // Display
  | 'list' | 'check_list' | 'progress' | 'steps_display' | 'image'
  | 'video' | 'logo_bar' | 'text_image' | 'photo_carousel' | 'accordion' | 'before_after'
  | 'skin_profile_summary'
  // Layout
  | 'container' | 'separator'
  // Components
  | 'stripe_checkout' | 'signature' | 'calendly' | 'html';

export type StepCategory = 'text' | 'input' | 'navigation' | 'buttons' | 'display' | 'layout' | 'components';

export interface StepOption {
  id: string;
  label: string;
  imageUrl?: string;
}

export interface StepSettings {
  align?: 'left' | 'center' | 'right';
  visibility?: 'all' | 'desktop' | 'mobile' | 'hidden';
  backgroundColor?: string;
  width?: 'S' | 'M' | 'L' | 'full';
  paddingTop?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;
}

export interface StepDesign {
  fontFamily?: string;
  fontSize?: number;
  fontSizeMobile?: number;
  fontWeight?: string;
  color?: string;
  lineHeight?: number;
  letterSpacing?: string;
  textTransform?: string;
  margin?: string;
}

export interface FlowStep {
  id: string;
  type: StepType;
  title: string;
  description?: string;
  required?: boolean;
  options?: StepOption[];
  placeholder?: string;
  settings?: StepSettings;
  design?: StepDesign;
  min?: number;
  max?: number;
  buttonText?: string;
  url?: string;
  htmlContent?: string;
  imageUrl?: string;
  images?: string[];
  videoUrl?: string;
  screenId?: string;
  multiSelect?: boolean;
  autoAdvance?: boolean;
  loaderSteps?: string[];
  loaderDuration?: number;
  linkUrl?: string;
}

export interface FlowScreen {
  id: string;
  name: string;
  stepIds: string[];
}

export interface ABVariant {
  id: string;
  name: string;
  weight: number;
  steps: FlowStep[];
}

export interface Flow {
  id: string;
  name: string;
  description: string;
  steps: FlowStep[];
  screens?: FlowScreen[];
  createdAt: string;
  updatedAt: string;
  responses: number;
  status: 'draft' | 'active' | 'paused';
  googleSheetsId?: string;
  customDomain?: string;
  abVariants?: ABVariant[];
  abTestEnabled?: boolean;
  logoUrl?: string;
  brandName?: string;
  accentColor?: string;
}

/** Helper: group steps by screenId into ordered screens */
export function getScreensFromFlow(flow: Flow): { id: string; steps: FlowStep[] }[] {
  if (flow.screens && flow.screens.length > 0) {
    return flow.screens.map(screen => ({
      id: screen.id,
      steps: screen.stepIds.map(sid => flow.steps.find(s => s.id === sid)!).filter(Boolean),
    }));
  }
  // Auto-group: each interactive block starts a new screen, text/display blocks attach to next interactive
  const screens: { id: string; steps: FlowStep[] }[] = [];
  let current: FlowStep[] = [];

  const isInteractive = (type: StepType) =>
    ['multiple_choice', 'picture_choice', 'input_field', 'email', 'phone_number',
     'date_picker', 'textarea', 'select', 'scale', 'icon_rating', 'range_slider',
     'upload', 'form', 'simple_checkbox', 'address_map'].includes(type);

  const isButton = (type: StepType) =>
    ['continue_button', 'submit_button', 'back_button', 'link_button', 'universal_button'].includes(type);

  for (const step of flow.steps) {
    if (step.screenId) {
      // If screenId is explicitly set, group by it
      const existing = screens.find(s => s.id === step.screenId);
      if (existing) {
        existing.steps.push(step);
      } else {
        if (current.length > 0) {
          screens.push({ id: `auto-${screens.length}`, steps: current });
          current = [];
        }
        screens.push({ id: step.screenId, steps: [step] });
      }
      continue;
    }

    if (step.type === 'loader' || step.type === 'list_loader') {
      if (current.length > 0) {
        current.push(step);
        screens.push({ id: `auto-${screens.length}`, steps: current });
        current = [];
      } else {
        screens.push({ id: `auto-${screens.length}`, steps: [step] });
      }
    } else if (isButton(step.type)) {
      current.push(step);
      screens.push({ id: `auto-${screens.length}`, steps: current });
      current = [];
    } else if (isInteractive(step.type)) {
      current.push(step);
      // Don't close screen yet — next text blocks might be footer text
    } else {
      // text, headline, image, etc.
      if (current.length > 0 && current.some(s => isInteractive(s.type))) {
        // We already have an interactive block, this text is footer — add to current
        current.push(step);
      } else {
        current.push(step);
      }
    }
  }
  if (current.length > 0) {
    screens.push({ id: `auto-${screens.length}`, steps: current });
  }

  return screens;
}
